"use client";
import { cx } from "class-variance-authority";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";

const CategoryList = ({
  tags,
  classes,
}) => {
  const sortedData = tags?.data?.sort((a, b) => a.attributes.name.localeCompare(b.attributes.name));
  const currentPage = usePathname();
  const router = useRouter();
  const containerRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [hasScrolled, setHasScrolled] = useState(false);

  const search = currentPage.split("/")[3];

  // Auto-scroll to active category
  useEffect(() => {
    if (containerRef.current && search) {
      const activeElement = containerRef.current.querySelector(`[data-category="${search.toLowerCase()}"]`);
      if (activeElement) {
        const container = containerRef.current;
        const containerWidth = container.offsetWidth;
        const elementLeft = activeElement.offsetLeft;
        const elementWidth = activeElement.offsetWidth;

        // Calculate the scroll position to center the active element
        const scrollPosition = elementLeft - containerWidth / 2 + elementWidth / 2;

        container.scrollTo({
          left: Math.max(0, scrollPosition),
          behavior: "smooth",
        });
      }
    }
  }, [search, tags]); // Re-run when search or tags change


  const handleStart = (e) => {
    // Only set dragging if starting from container, not from clickable items
    if (e.target.closest(".scroll-item")) {
      return;
    }

    setIsDragging(true);
    setHasScrolled(false);

    const pageX = e.type === "mousedown" ? e.pageX : e.touches[0].pageX;
    setStartX(pageX - containerRef.current.offsetLeft);
    setScrollLeft(containerRef.current.scrollLeft);
  };

  const handleMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();

    const pageX = e.type === "mousemove" ? e.pageX : e.touches[0].pageX;
    const x = pageX - containerRef.current.offsetLeft;

    // Mark as scrolled if moved more than 3px (reduced threshold)
    if (Math.abs(x - startX) > 3) {
      setHasScrolled(true);
    }

    // Simple scroll without any animation or momentum
    const walk = (x - startX) * 1;
    containerRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleEnd = (e) => {
    setIsDragging(false);

    // Small delay to ensure hasScrolled state is properly set
    setTimeout(() => {
      setHasScrolled(false);
    }, 100);
  };

  const handleLinkClick = (e, href) => {
    e.preventDefault();
    e.stopPropagation();

    // Always allow navigation on direct clicks
    router.push(href);
  };

  return (
    <>
      <style jsx>{`
        .custom-scroll-container {
          scrollbar-width: none;
          -ms-overflow-style: none;
          cursor: grab;
          user-select: none;
        }

        .custom-scroll-container.dragging {
          cursor: grabbing;
        }

        .custom-scroll-container::-webkit-scrollbar {
          display: none;
        }

        .scroll-item {
          flex-shrink: 0;
          min-width: fit-content;
          pointer-events: auto;
        }

        /* Disable pointer events while dragging */
        .custom-scroll-container.dragging .scroll-item {
          pointer-events: none;
        }

        /* Keep scroll items clickable always */
        .scroll-item {
          cursor: pointer;
        }

        /* Prevent text selection */
        .scroll-item * {
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
          user-select: none;
        }

        /* Prevent tap highlight on mobile */
        .scroll-item {
          -webkit-tap-highlight-color: transparent;
        }

        /* Disable hover effects while dragging */
        .custom-scroll-container.dragging .scroll-item:hover {
          background-color: transparent !important;
          color: inherit !important;
        }
      `}</style>

      <div
        ref={containerRef}
        className={cx(
          "flex gap-3 w-full border-b border-b-[#E9E9E9] overflow-x-auto custom-scroll-container",
          isDragging ? "dragging" : "",
          classes
        )}
        style={{ cursor: isDragging ? "grabbing" : "default" }}
      >
        <div
          data-href="/blog"
          data-category="all"
          className={`${
            currentPage == "/blog"
              ? "text-black bg-[#400ead28] rounded-md"
              : "text-[#666666] hover:text-black hover:bg-[#400ead28] rounded-md"
          } cursor-pointer text-nowrap mt-5 text-sm transition-all duration-300 ease-in-out w-fit rounded-sm border-b border-b-transparent mb-3 py-1 px-1.5 scroll-item`}
          onClick={(e) => handleLinkClick(e, "/blog")}
        >
          <span className="leading-4">All</span>
        </div>

        {tags?.map((tag, ind) => {
          const isActive = tag?.uid.toLowerCase() === search?.toLowerCase();
          return (
            <div
              key={tag?.uid || ind}
              data-href={`/blog/category/${tag?.uid}`}
              data-category={tag?.uid.toLowerCase()}
              className="scroll-item"
              onClick={(e) => handleLinkClick(e, `/blog/category/${tag?.uid}`)}
            >
              <h2
                className={cx(
                  "cursor-pointer text-nowrap mt-5 text-sm transition-all duration-300 ease-in-out w-fit rounded-sm border-b border-b-transparent mb-3 py-1 px-1.5",
                  isActive
                    ? "text-black bg-[#400ead28] rounded-md"
                    : "text-[#666666] hover:text-black hover:bg-[#400ead28] rounded-md"
                )}
              >
                <span className="leading-4">{tag?.name}</span>
              </h2>
            </div>
          );
        })}
      </div>
    </>
  );
};

export default CategoryList;
